import { type ReactElement } from 'react';
import RankedList, { type RankedListColumn, type RankedListPrimary } from '@/design/RankedList/RankedList';
import { formatCount, formatPercent, formatUsd } from '@/shared/analytics/MetricFormatting';
import { SEATS_BUCKET_ID, type SpendCostRow } from './SpendCostRows';

const LINES_WIDTH = 60;
const SPEND_WIDTH = 84;

/** Drill target: a provider row → its records; the seats bucket → that provider's seat costs; a model row → all spend for that model across providers. */
function hrefFor(row: SpendCostRow): string {
    if (row.kind === 'provider') return `/cost/provider/${encodeURIComponent(row.connectionId)}`;
    if (row.modelId === SEATS_BUCKET_ID) return `/cost/provider/${encodeURIComponent(row.connectionId)}/seats`;
    return `/cost/model/${encodeURIComponent(row.modelId ?? '')}`;
}

/** The Cost-by-Provider ranked list: providers by spend with a bar + share, expanding to their by-model breakdown. */
export default function SpendCostRankedList({ rows }: { rows: SpendCostRow[] }): ReactElement {
    const primary: RankedListPrimary<SpendCostRow> = {
        key: 'label',
        header: 'Provider / model',
        label: (row) => row.label,
        subline: (row) => (row.subLabel !== undefined && row.subLabel !== row.label ? row.subLabel : undefined),
        fill: (row) => row.widthPct,
        share: (row) => formatPercent(row.share),
        sort: { get: (row) => row.label, initialDir: 'asc' },
    };

    const columns: RankedListColumn<SpendCostRow>[] = [
        { key: 'lines', header: 'Lines', width: LINES_WIDTH, render: (row) => formatCount(row.lineCount), sort: { get: (row) => row.lineCount, initialDir: 'desc' }, visibleFrom: 'sm', size: 'xs' },
        { key: 'total', header: 'Spend', width: SPEND_WIDTH, render: (row) => formatUsd(row.total), sort: { get: (row) => row.total, initialDir: 'desc' } },
    ];

    return (
        <RankedList
            rows={rows}
            primary={primary}
            columns={columns}
            rowKey={(row) => row.id}
            accent="teal"
            initialSort={{ key: 'total', dir: 'desc' }}
            hrefFor={hrefFor}
            unitPlural="providers"
            childrenFor={(row) => row.children}
            accentFor={(_row, depth) => (depth === 0 ? 'teal' : 'cyan')}
        />
    );
}
